import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import { setBaseTokens, clearAll, getAccessToken, isImpersonando } from '../services/authStorage';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);

  const carregarPerfil = async () => {
    try {
      const res = await api.get('/membros-equipe/me/');
      setPerfil(res.data);
    } catch {
      setPerfil(null);
    }
  };

  const [isImpersonating, setIsImpersonating] = useState(false);

  const verificarAdmin = async () => {
    try {
      const res = await api.get('/auth/me/');
      setIsSuperAdmin(res.data.is_superuser || res.data.is_staff);
      setIsImpersonating(res.data.is_impersonating || isImpersonando());
    } catch {
      setIsSuperAdmin(false);
    }
  };

  const carregarModulos = async () => {
    try {
      const res = await api.get('/modulos/');
      setModulos(res.data);
    } catch {
      setModulos([]);
    }
  };

  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      setUser({ token });
      Promise.all([
        carregarPerfil(),
        verificarAdmin(),
        carregarModulos(),
      ]).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/token/`,
      { username, password }
    );
    setBaseTokens(response.data.access, response.data.refresh);
    setUser({ token: response.data.access });
    await Promise.all([
      carregarPerfil(),
      verificarAdmin(),
      carregarModulos(),
    ]);
    return response;
  };

  const logout = () => {
    clearAll();
    setUser(null);
    setPerfil(null);
    setIsSuperAdmin(false);
    setModulos([]);
    window.location.href = '/login';
  };

  const isDiretor = perfil?.perfil === 'diretor';
  const isGestor = isSuperAdmin || isDiretor || perfil?.perfil === 'gestor';
  const isFinanceiro = perfil?.perfil === 'financeiro';
  const isComercial = perfil?.perfil === 'comercial';
  const isMarketing = perfil?.perfil === 'marketing';

  const moduloAtivo = (chave) => {
    if (isSuperAdmin) return true;
    const modulo = modulos.find((m) => m.chave === chave);
    return modulo ? modulo.ativo : false;
  };

  const getPrevisao = (chave) => {
    const modulo = modulos.find((m) => m.chave === chave);
    return modulo?.previsao || null;
  };

  // Novo controle de acesso por usuário: verifica se o módulo (pela chave,
  // ex: 'financeiro') está na lista de módulos liberados para esse usuário
  // no cadastro de Membro da Equipe. Superadmin sempre tem acesso a tudo.
  // Ainda não é usado em nenhuma tela — é só a função disponível, pronta
  // para as próximas etapas.
  const temAcesso = (chave) => {
    if (isSuperAdmin) return true;
    return perfil?.modulos_liberados?.includes(chave) || false;
  };

  return (
    <AuthContext.Provider value={{
      user, perfil, login, logout, loading,
      isGestor, isSuperAdmin, isDiretor, isFinanceiro, isComercial, isMarketing,
      modulos, moduloAtivo, getPrevisao, temAcesso, isImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}